import type { WealthicaConnection } from '@/providers/wealthica2/types';
import { supabase } from '@/integrations/supabase/client';
import { resolveStableIdentifier } from '@/providers/wealthica2/utils/identifiers';
import { createInitialConnectionState } from './state-utils';
import { storeLogger, EVENTS } from './logging';

type UnknownRecord = Record<string, unknown>;

type SetFn = (partial: Partial<LocalState> | ((state: LocalState) => Partial<LocalState>)) => void;
type GetFn = () => LocalState;

type LocalState = {
  connections: Map<string, ReturnType<typeof createInitialConnectionState>>;
  activeConnectionId: string | null;
  isInitializing: boolean;
  initializationError: string | null;
};

const asRecord = (u: unknown): UnknownRecord => (u && typeof u === 'object') ? (u as UnknownRecord) : {};

export const createHydrationActions = (set: unknown, get: unknown) => ({
  // === DATABASE SYNCHRONIZATION ===
  initializeFromDatabase: async (userId: string) => {
    const state = (get as GetFn)();

    // Skip if already initialized or in progress
    if (state.connections.size > 0 || state.isInitializing) {
      storeLogger.info('multiWealthica2Store', '🔄 MULTI-STORE HYDRATION: Skipping database init - already has connections or initializing', {
        connectionCount: state.connections.size,
        isInitializing: state.isInitializing
      });
      return;
    }

    try {
      storeLogger.info('multiWealthica2Store', '🔄 MULTI-STORE HYDRATION: Initializing from database', { userId });
      // Centralized event logging
      storeLogger.info('multiWealthica2Store', EVENTS.HYDRATION_START, { userId });

      // Set initializing state
      (set as SetFn)((s) => ({ ...s, isInitializing: true, initializationError: null }));

      // Use the centralized supabase client - select only from wealthica_connections
      const { data: connections, error: connectionError } = await supabase
        .from('wealthica_connections')
        .select('id, institution_id, institution_identifier, provider_name, connection_status, last_sync_at, error_message, created_at, updated_at')
        .eq('user_id', userId);

      if (connectionError) {
        throw new Error(`Failed to fetch connections: ${connectionError.message}`);
      }

      storeLogger.info('multiWealthica2Store', '🔄 MULTI-STORE HYDRATION: Fetched connections from database', {
        connectionCount: Array.isArray(connections) ? connections.length : 0,
        connections: (Array.isArray(connections) ? connections : []).map((c) => {
          const rec = asRecord(c);
          return {
            id: String(rec.institution_id ?? ''),
            identifier: String(rec.institution_identifier ?? ''),
            provider: String(rec.provider_name ?? 'wealthica')
          };
        })
      });

      // Consolidate rotating IDs by stable identifier for ALL providers (demo and non-demo)
      const latestByStable = new Map<string, UnknownRecord>();
      const idsByStable = new Map<string, string[]>();
      for (const dbConnection of (Array.isArray(connections) ? connections : [])) {
        const rec = asRecord(dbConnection);
        const stableKey = resolveStableIdentifier({
          providerName: String(rec.provider_name ?? 'wealthica'),
          institutionIdentifier: String(rec.institution_identifier ?? ''),
          institutionId: String(rec.institution_id ?? '')
        });
        const currTime = new Date(String(rec.updated_at ?? rec.created_at ?? 0)).getTime();
        const prev = latestByStable.get(stableKey);
        const prevTime = prev ? new Date(String(prev.updated_at ?? prev.created_at ?? 0)).getTime() : -1;
        if (!prev || currTime >= prevTime) {
          latestByStable.set(stableKey, rec);
        }
        const arr = idsByStable.get(stableKey) || [];
        arr.push(String(rec.institution_id ?? ''));
        idsByStable.set(stableKey, arr);
      }

      // Best-effort: re-key caches from any old rotating IDs to the latest ID per stable identifier
      try {
        import('../../providers/wealthica2/utils/cache-management').then(({ rekeyInstitutionCaches }) => {
          for (const [stableKey, latestConn] of latestByStable.entries()) {
            const latestId = String(latestConn.institution_id ?? '');
            const ids = idsByStable.get(stableKey) || [];
            ids.filter((id) => id && id !== latestId).forEach((oldId) => {
              try {
                rekeyInstitutionCaches(oldId, latestId);
                storeLogger.info('multiWealthica2Store', EVENTS.CACHE_REKEY, {
                  phase: 'db_init_rekey',
                  stableIdentifier: stableKey,
                  from: oldId,
                  to: latestId
                });
              } catch { /* noop */ }
            });
          }
        }).catch(() => { /* noop */ });
      } catch { /* noop */ }

      // Build new connections from latestByStable
      const newConnections = new Map<string, ReturnType<typeof createInitialConnectionState>>();
      for (const [, dbConnection] of latestByStable.entries()) {
        const institutionId = String(dbConnection.institution_id ?? '');
        const providerName = String(dbConnection.provider_name ?? 'wealthica');

        const connectionData: WealthicaConnection = {
          institutionId,
          providerName,
          originalProvider: providerName,
          institutionIdentifier: String(dbConnection.institution_identifier ?? institutionId),
          timestamp: Date.now(),
          state: 'connected'
        };

        // Server-only token model: do not read bearer tokens from frontend storage
        const token = '';
        const connectionState = createInitialConnectionState(connectionData, token, institutionId);
        connectionState.status = String(dbConnection.connection_status ?? '') === 'connected' ? 'connected' : 'idle';
        connectionState.isConnected = String(dbConnection.connection_status ?? '') === 'connected';
        newConnections.set(institutionId, connectionState);
      }

      // Update store with database connections
      (set as SetFn)((s) => ({
        ...s,
        connections: newConnections,
        activeConnectionId: newConnections.size > 0 ? Array.from(newConnections.keys())[0] : null,
        isInitializing: false,
        initializationError: null
      }));

      // Fail-safe: If persistence preference is OFF, ensure DB data is removed now that
      // connections are available (covers cases where toggle OFF happened before hydration)
      try {
        const storeAny = (get as unknown as () => any)();
        const persistPref: boolean = !!storeAny.persistDataPreference;
        if (!persistPref && newConnections.size > 0) {
          storeLogger.info('multiWealthica2Store', '💾 PERSISTENCE: Enforcing OFF preference on hydration - deleting per-connection data');
          for (const [, conn] of newConnections) {
            try {
              if (conn?.connectionData) {
                await storeAny.deleteAccountData?.(userId, conn.connectionData, { preserveConnection: true, deleteMode: 'stop_syncing', suppressSuccessToast: true });
              }
            } catch (delErr) {
              storeLogger.warn('multiWealthica2Store', '💾 PERSISTENCE: Hydration cleanup failed for a connection', { error: delErr instanceof Error ? delErr.message : String(delErr) });
            }
          }
        }
      } catch { /* noop */ }

      storeLogger.info('multiWealthica2Store', '✅ MULTI-STORE HYDRATION: Successfully initialized from database', {
        connectionCount: newConnections.size,
        activeConnectionId: newConnections.size > 0 ? Array.from(newConnections.keys())[0] : null
      });
      // Centralized event logging
      storeLogger.info('multiWealthica2Store', EVENTS.HYDRATION_COMPLETE, {
        connectionCount: newConnections.size,
        activeConnectionId: newConnections.size > 0 ? Array.from(newConnections.keys())[0] : null
      });

    } catch (error: unknown) {
      storeLogger.error('multiWealthica2Store', '🚨 MULTI-STORE: Failed to initialize from database', {
        error: error instanceof Error ? error.message : String(error),
        userId
      });

      (set as SetFn)((s) => ({
        ...s,
        isInitializing: false,
        initializationError: error instanceof Error ? error.message : 'Failed to load connections'
      }));
    }
  }
});

// Schedules an auto-refetch of accounts shortly after hydration completes.
// Accepts a getter function to avoid circular imports with the store.
export const schedulePostHydrationAutoRefetch = (getStoreState: () => unknown) => {
  try {
    setTimeout(() => {
      try {
        const store = getStoreState() as UnknownRecord;
        const connections = store.connections as Map<string, unknown> | undefined;
        const persistPref = !!(store as UnknownRecord).persistDataPreference;
        if (connections && connections.size > 0) {
          // Enforce OFF preference if needed for persisted connections
          if (!persistPref) {
            // Retry up to 5 times with 400ms delay to wait for userId
            let attempts = 0;
            const maxAttempts = 5;
            const delayMs = 400;
            const enforceOffDeleteWithRetry = async () => {
              try {
                const { useAuthStore } = await import('@/stores/authStore');
                const userId = useAuthStore.getState().user?.id;
                if (!userId) {
                  attempts += 1;
                  if (attempts <= maxAttempts) {
                    setTimeout(enforceOffDeleteWithRetry, delayMs);
                  } else {
                    console.warn('💾 PERSISTENCE: Post-hydration OFF enforcement aborted - userId not available after retries');
                  }
                  return;
                }

                storeLogger.info('multiWealthica2Store', EVENTS.HYDRATION_AUTO_REFETCH, {
                  phase: 'enforce_off_delete',
                  connectionCount: connections.size,
                  attempts
                });

                for (const [, conn] of connections) {
                  try {
                    const c = conn as UnknownRecord;
                    const connectionData = c.connectionData as UnknownRecord | undefined;
                    if (connectionData) {
                      await (store as unknown as { deleteAccountData: (userId: string, connectionData: unknown, options: unknown) => Promise<void> })
                        .deleteAccountData(userId, connectionData, { preserveConnection: true, deleteMode: 'stop_syncing', suppressSuccessToast: true });
                    }
                  } catch (delErr) {
                    console.warn('💾 PERSISTENCE: Post-hydration OFF enforcement failed for a connection', { error: delErr });
                  }
                }
              } catch { /* noop */ }
            };
            enforceOffDeleteWithRetry();
          }

          // Define the actual background fetch procedure (unchanged behavior)
          const proceedWithFetch = () => {
            try {
              import('@/providers/wealthica2/utils/storage').then(({ WealthicaStorage }) => {
                const forcedId = WealthicaStorage.getItem('WEALTHICA2_FORCE_FETCH_INSTITUTION_ID');
                const hasForced = !!forcedId && connections?.has(String(forcedId));
                if (hasForced && forcedId) {
                  storeLogger.info('multiWealthica2Store', EVENTS.HYDRATION_AUTO_REFETCH, {
                    phase: 'forced_institution_fetch',
                    institutionId: forcedId
                  });
                  (store as unknown as { fetchAccountsForInstitution: (id: string, isNav?: boolean, force?: boolean) => Promise<void> })
                    .fetchAccountsForInstitution(String(forcedId), true, true)
                    .finally(() => {
                      try { WealthicaStorage.removeItem('WEALTHICA2_FORCE_FETCH_INSTITUTION_ID'); } catch { /* noop */ }
                    });
                } else {
                  // Fallback to batch auto-refetch (non-forced)
                  storeLogger.info('multiWealthica2Store', EVENTS.HYDRATION_AUTO_REFETCH, {
                    connectionCount: connections.size
                  });
                  (store as unknown as { fetchAccountsForAllConnections: (isNavigation?: boolean) => void })
                    .fetchAccountsForAllConnections(false);
                }
              }).catch(() => {
                // If storage import fails, fallback to batch auto-refetch
                storeLogger.info('multiWealthica2Store', EVENTS.HYDRATION_AUTO_REFETCH, {
                  connectionCount: connections.size,
                  phase: 'storage_import_failed_fallback'
                });
                (store as unknown as { fetchAccountsForAllConnections: (isNavigation?: boolean) => void })
                  .fetchAccountsForAllConnections(false);
              });
            } catch {
              // Fallback to batch auto-refetch on any unexpected error
              storeLogger.info('multiWealthica2Store', EVENTS.HYDRATION_AUTO_REFETCH, {
                connectionCount: connections.size,
                phase: 'forced_fetch_error_fallback'
              });
              (store as unknown as { fetchAccountsForAllConnections: (isNavigation?: boolean) => void })
                .fetchAccountsForAllConnections(false);
            }
          };

          // Defer background auto-refetch until a session exists (userId present)
          const MAX_ATTEMPTS = 5;
          let attempts = 0;
          const deferUntilSession = async (): Promise<void> => {
            try {
              const { useAuthStore } = await import('@/stores/authStore');
              const userId = useAuthStore.getState().user?.id;
              if (userId) {
                proceedWithFetch();
                return;
              }
            } catch { /* noop */ }

            attempts += 1;
            if (attempts <= MAX_ATTEMPTS) {
              storeLogger.info('multiWealthica2Store', EVENTS.HYDRATION_AUTO_REFETCH, {
                phase: 'deferred_until_session',
                attempt: attempts
              });
              setTimeout(deferUntilSession, 400);
            } else {
              // Give up quietly to avoid noisy errors without a session
              storeLogger.info('multiWealthica2Store', EVENTS.HYDRATION_AUTO_REFETCH, {
                phase: 'session_not_ready_skip'
              });
            }
          };

          deferUntilSession();
          
        }
      } catch (innerErr) {
        console.warn(' MULTI-STORE HYDRATION: Auto-refetch scheduling failed', { error: innerErr });
      }
    }, 500); // Short delay to ensure store is fully initialized
  } catch (e) {
    console.warn(' MULTI-STORE HYDRATION: Failed to schedule post-hydration auto-refetch', { error: e });
    console.warn('⚠️ MULTI-STORE HYDRATION: Failed to schedule post-hydration auto-refetch', { error: e });
  }
};

// Centralized rehydration merge helper to reconstruct Maps and set flags consistently
export const rehydratePersistedState = (persistedState: unknown, currentState: unknown): unknown => {
  try {
    const p = asRecord(persistedState);
    const c = asRecord(currentState);
    const persistedConnections = Array.isArray(p.connections) ? p.connections.length : (p.connections instanceof Map ? p.connections.size : 0);
    storeLogger.debug('multiWealthica2Store', EVENTS.HYDRATION_START, {
      persistedConnections,
      hasPersistedState: !!persistedState,
      persistedKeys: persistedState ? Object.keys(p) : null
    });

    let connectionsMap: Map<string, unknown>;
    const rawConns = p.connections as unknown;
    if (rawConns instanceof Map) {
      connectionsMap = new Map(rawConns as Map<string, unknown>);
    } else if (Array.isArray(rawConns)) {
      connectionsMap = new Map(rawConns as Array<[string, unknown]>);
    } else {
      connectionsMap = new Map<string, unknown>();
    }

    const mergedState = {
      ...c,
      ...p,
      connections: connectionsMap,
      hydrationCompleted: true
    } as UnknownRecord;

    storeLogger.debug('multiWealthica2Store', EVENTS.HYDRATION_COMPLETE, {
      finalConnectionCount: connectionsMap.size,
      activeConnectionId: (mergedState as UnknownRecord).activeConnectionId
    });

    return mergedState;
  } catch (err) {
    console.warn('⚠️ MULTI-STORE HYDRATION: Rehydrate failed, falling back to currentState', { error: err });
    return currentState;
  }
};
